import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminEspecialidadesQuery } from '../../queries/appQueries';
import type { Especialidad as EspecialidadCompartida } from '../../services/especialidadService';
import {
  Button,
  Table,
  Text,
  Group,
  Stack,
  Select,
  Pagination,
  Modal,
  Textarea,
  Checkbox,
  ActionIcon,
  Alert,
  Card,
  Progress,
  List,
  Box,
  Menu,
  Tooltip,
} from '@mantine/core';
import {
  IconCards,
  IconPlus,
  IconEdit,
  IconTrash,
  IconFilter,
  IconInfoCircle,
  IconCheck,
  IconX,
  IconUpload,
  IconFileSpreadsheet,
  IconAlertCircle,
  IconSettings,
  IconChevronDown,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useMantineColorScheme } from '@mantine/core';
import CountUpNumber from '../../components/animations/CountUpNumber';
import { SimpleGrid } from '@mantine/core';
import { flashcardService } from '../../services/flashcardService';
import { FlashcardDTO, FlashcardCreateUpdateDTO } from '../../Models/Flashcard';
import { Especialidad } from '../../Models/Especialidad';
import { temaService, Tema } from '../../services/temaService';
import AdminPageHeader from '../../components/AdminPageHeader';
import PageTransition from '../../components/animations/PageTransition';
import PageSkeleton from '../../components/ui/PageSkeleton';
import SearchInput from '../../components/ui/SearchInput';

const AdminFlashcardsPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  // Paleta sobria unificada con Reactivos/Dashboard.
  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = dark ? '#e2e8f0' : '#2d2a26';
  const mutedColor = dark ? '#94a3b8' : '#8c8478';
  const colEspecialidad = dark ? '#8aa9d6' : '#5c7bb0';
  const colTema = dark ? '#86b8a6' : '#4f8a74';
  const colActiva = dark ? '#86b8a6' : '#4f8a74';
  const colInactiva = dark ? '#d29ba0' : '#b5565e';
  const primaryBtn = { root: { backgroundColor: textColor, color: dark ? '#1e293b' : '#f7f3ee' } };
  const outlineBtn = { root: { borderColor: textColor, color: textColor, backgroundColor: 'transparent' } };
  const resumenPregunta = (texto: string, palabras = 6) => {
    const w = texto.trim().split(/\s+/);
    return w.length <= palabras ? texto : w.slice(0, palabras).join(' ') + '…';
  };

  // Estados principales
  const [flashcards, setFlashcards] = useState<FlashcardDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para filtros y paginación
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [filterEspecialidad, setFilterEspecialidad] = useState<string | null>(null);
  const [filterTema, setFilterTema] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Estados para el formulario
  const [opened, { open, close }] = useDisclosure(false);
  const [editingFlashcard, setEditingFlashcard] = useState<FlashcardDTO | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // CSV import
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResultado, setCsvResultado] = useState<{ insertados: number; omitidos: number; errores: string[] } | null>(null);
  
  // Datos auxiliares
  const especialidadesResult = useQuery(adminEspecialidadesQuery());
  const especialidades = (especialidadesResult.data as EspecialidadCompartida[] | undefined) ?? [];
  const [temas, setTemas] = useState<Tema[]>([]); // Para el formulario
  const [temasFilter, setTemasFilter] = useState<Tema[]>([]); // Para el filtro
  const [selectedEspecialidadId, setSelectedEspecialidadId] = useState<number | null>(null);
  
  // Formulario
  const [formData, setFormData] = useState<FlashcardCreateUpdateDTO>({
    pregunta: '',
    respuesta: '',
    explicacion: '',
    especialidadId: 0,
    temaId: 0,
    activo: true
  });

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  }, []);

  // Cargar temas cuando se selecciona especialidad en el formulario
  useEffect(() => {
    console.log('🟡 useEffect selectedEspecialidadId (MODAL) cambió:', selectedEspecialidadId);
    if (selectedEspecialidadId) {
      console.log('Cargando temas para modal...');
      const cargarTemas = async () => {
        try {
          const temasData = await temaService.getTemasByEspecialidad(selectedEspecialidadId);
          console.log('Temas cargados para modal:', temasData);
          setTemas(temasData);
        } catch (error) {
          console.error('Error al cargar temas del modal:', error);
          setTemas([]);
        }
      };
      cargarTemas();
    } else {
      console.log('🟡 Limpiando temas del modal');
      setTemas([]);
    }
  }, [selectedEspecialidadId]);

  // Cargar temas cuando se selecciona especialidad en el filtro
  useEffect(() => {
    console.log('🔄 useEffect filterEspecialidad cambió:', filterEspecialidad, 'tipo:', typeof filterEspecialidad);
    // Verificar si es un valor válido (no vacío, no null)
    if (filterEspecialidad && filterEspecialidad !== '') {
      console.log('Cargando temas para especialidad:', filterEspecialidad);
      const cargarTemasFiltro = async () => {
        try {
          const temasData = await temaService.getTemasByEspecialidad(parseInt(filterEspecialidad));
          console.log('Temas cargados del filtro:', temasData);
          setTemasFilter(temasData);
        } catch (error) {
          console.error('Error al cargar temas del filtro:', error);
          setTemasFilter([]);
        }
      };
      cargarTemasFiltro();
    } else {
      console.log('🔄 Limpiando temas del filtro');
      setTemasFilter([]);
      setFilterTema(null);
    }
  }, [filterEspecialidad]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const flashcardsData = await flashcardService.obtenerFlashcards({});
      setFlashcards(flashcardsData);
      setError(null);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error al cargar los datos. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const loadTemas = async () => {
    if (!selectedEspecialidadId) return;
    
    console.log('🔍 loadTemas (MODAL) - selectedEspecialidadId:', selectedEspecialidadId);
    
    try {
      const temasData = await temaService.getTemasByEspecialidad(selectedEspecialidadId);
      console.log('Temas cargados para modal:', temasData);
      setTemas(temasData);
    } catch (error) {
      console.error('Error al cargar temas del modal:', error);
    }
  };

  const loadTemasFilter = async () => {
    if (!filterEspecialidad) return;
    
    console.log('🔍 loadTemasFilter - filterEspecialidad:', filterEspecialidad);
    
    try {
      const temasData = await temaService.getTemasByEspecialidad(parseInt(filterEspecialidad));
      console.log('Temas cargados del filtro:', temasData);
      setTemasFilter(temasData);
    } catch (error) {
      console.error('Error al cargar temas del filtro:', error);
    }
  };

  // Filtrar flashcards
  const filteredFlashcards = flashcards.filter(flashcard => {
    const matchesSearch = flashcard.pregunta.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         flashcard.respuesta.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEspecialidad = !filterEspecialidad || flashcard.especialidadId.toString() === filterEspecialidad;
    const matchesTema = !filterTema || flashcard.temaId.toString() === filterTema;
    
    return matchesSearch && matchesEspecialidad && matchesTema;
  });

  // Paginación
  const totalPages = Math.ceil(filteredFlashcards.length / itemsPerPage);
  const paginatedFlashcards = filteredFlashcards.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Volver a la primera página al cambiar filtros/búsqueda/tamaño.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterEspecialidad, filterTema, itemsPerPage]);

  // Filtro desplegable estilo Reactivos (botón que oculta/despliega opciones).
  const filterMenu = (opts: {
    label: string;
    selectedId: string | null;
    items: Array<{ id: number; nombre: string }>;
    onSelect: (id: string | null) => void;
    disabled?: boolean;
  }) => {
    const selected = opts.items.find((i) => i.id.toString() === opts.selectedId);
    return (
      <Menu shadow="md" width={250} position="bottom-start" withinPortal disabled={opts.disabled}>
        <Menu.Target>
          <Button
            variant={opts.selectedId ? 'light' : 'default'}
            color="gray"
            radius="md"
            disabled={opts.disabled}
            leftSection={<IconFilter size={16} />}
            rightSection={<IconChevronDown size={14} />}
          >
            {selected ? selected.nombre : opts.label}
          </Button>
        </Menu.Target>
        <Menu.Dropdown style={{ maxHeight: 320, overflowY: 'auto' }}>
          <Menu.Item
            leftSection={!opts.selectedId ? <IconCheck size={14} /> : <Box style={{ width: 14 }} />}
            onClick={() => opts.onSelect(null)}
          >
            {opts.label} (todas)
          </Menu.Item>
          <Menu.Divider />
          {opts.items.map((it) => {
            const sel = opts.selectedId === it.id.toString();
            return (
              <Menu.Item key={it.id} onClick={() => opts.onSelect(it.id.toString())} rightSection={sel ? <IconCheck size={14} /> : null}>
                <Text size="sm" fw={sel ? 700 : 500}>{it.nombre}</Text>
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
    );
  };
  const hayFiltrosActivos = !!(filterEspecialidad || filterTema || searchTerm.trim());

  // Manejar formulario
  const handleOpenForm = (flashcard?: FlashcardDTO) => {
    if (flashcard) {
      setEditingFlashcard(flashcard);
      setFormData({
        pregunta: flashcard.pregunta,
        respuesta: flashcard.respuesta,
        explicacion: flashcard.explicacion || '',
        especialidadId: flashcard.especialidadId,
        temaId: flashcard.temaId,
        activo: flashcard.activo
      });
      setSelectedEspecialidadId(flashcard.especialidadId);
    } else {
      setEditingFlashcard(null);
      setFormData({
        pregunta: '',
        respuesta: '',
        explicacion: '',
        especialidadId: 0,
        temaId: 0,
        activo: true
      });
      setSelectedEspecialidadId(null);
    }
    open();
  };

  const handleSubmit = async () => {
    if (!formData.pregunta || !formData.respuesta || !formData.especialidadId || !formData.temaId) {
      notifications.show({
        title: 'Error',
        message: 'Por favor completa todos los campos obligatorios',
        color: 'red'
      });
      return;
    }

    try {
      setFormLoading(true);
      
      if (editingFlashcard) {
        await flashcardService.actualizarFlashcard(editingFlashcard.id, formData);
        notifications.show({
          title: 'Éxito',
          message: 'Flashcard actualizada correctamente',
          color: 'green'
        });
      } else {
        await flashcardService.crearFlashcard(formData);
        notifications.show({
          title: 'Éxito',
          message: 'Flashcard creada correctamente',
          color: 'green'
        });
      }
      
      close();
      loadInitialData();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Error al guardar la flashcard',
        color: 'red'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta flashcard?')) return;
    
    try {
      await flashcardService.eliminarFlashcard(id);
      notifications.show({
        title: 'Éxito',
        message: 'Flashcard eliminada correctamente',
        color: 'green'
      });
      loadInitialData();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Error al eliminar la flashcard',
        color: 'red'
      });
    }
  };

  const handleImportarCSV = async () => {
    if (!csvFile) return;
    setCsvUploading(true);
    setCsvResultado(null);
    try {
      const data = new FormData();
      data.append('file', csvFile);
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/flashcards/importar-csv', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: data,
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Error al importar');
      setCsvResultado(result);
      notifications.show({ title: 'Importación exitosa', message: `${result.insertados} tarjetas importadas`, color: 'green' });
      loadInitialData();
    } catch (e: any) {
      notifications.show({ title: 'Error', message: e.message, color: 'red' });
    } finally {
      setCsvUploading(false);
    }
  };

  if (loading) {
    return <PageSkeleton variant="grid" count={3} />;
  }

  if (error) {
    return (
      <Box style={{ flex: 1, padding: '16px 24px' }}>
        <Alert icon={<IconInfoCircle size="1rem" />} title="Error" color="red">
          {error}
        </Alert>
      </Box>
    );
  }

  const statsCards = [
    { icon: IconCards, value: flashcards.length, label: 'Total flashcards', duration: 2500 },
    { icon: IconCheck, value: flashcards.filter(f => f.activo).length, label: 'Activas', duration: 2000 },
    { icon: IconX, value: flashcards.filter(f => !f.activo).length, label: 'Inactivas', duration: 2000 },
  ];

  return (
    <PageTransition type="medical" duration={800}>
    <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto' }}>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center">
          <AdminPageHeader
            title="Anki"
            subtitle="Gestiona tarjetas de estudio para estudiantes"
          />
          <Group>
            <Button leftSection={<IconUpload size={16} />} variant="default" styles={outlineBtn} onClick={() => { setCsvFile(null); setCsvResultado(null); setCsvModalOpen(true); }}>
              Importar CSV
            </Button>
            <Button leftSection={<IconPlus size={16} />} styles={primaryBtn} onClick={() => handleOpenForm()}>
              Nueva Flashcard
            </Button>
          </Group>
        </Group>

        {/* Estadísticas rápidas (paleta sobria) */}
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
          {statsCards.map((s) => (
            <Card key={s.label} withBorder radius="lg" p="lg" style={cardStyle}>
              <Stack gap="sm" align="stretch">
                <s.icon size={20} color={textColor} style={{ flexShrink: 0 }} />
                <Box style={{ minWidth: 0 }}>
                  <CountUpNumber
                    value={s.value}
                    duration={s.duration}
                    style={{ color: textColor, fontSize: '1.6rem', fontWeight: 700, fontFamily: 'Space Grotesk, Inter, sans-serif', textAlign: 'left' }}
                  />
                  <Text size="xs" ta="left" style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                    {s.label}
                  </Text>
                </Box>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>

        {/* Búsqueda + filtros desplegables (sin card, estilo Reactivos) */}
        <Group gap="sm" align="center" wrap="wrap">
          <SearchInput
            placeholder="Buscar en pregunta o respuesta..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          {filterMenu({
            label: 'Especialidad',
            selectedId: filterEspecialidad,
            items: especialidades,
            onSelect: (id) => { setFilterEspecialidad(id); setFilterTema(null); },
          })}
          {filterMenu({
            label: 'Tema',
            selectedId: filterTema,
            items: temasFilter,
            onSelect: setFilterTema,
            disabled: !filterEspecialidad,
          })}
          {hayFiltrosActivos && (
            <Button variant="subtle" color="gray" onClick={() => { setSearchTerm(''); setFilterEspecialidad(null); setFilterTema(null); }}>
              Limpiar
            </Button>
          )}
        </Group>

        {/* Tabla de flashcards */}
        {filteredFlashcards.length === 0 ? (
          <Card withBorder radius="lg" p="xl" style={cardStyle}>
            <Stack align="center" gap="md">
              <IconCards size={48} style={{ opacity: 0.3, color: mutedColor }} />
              <Text style={{ color: mutedColor }}>
                {hayFiltrosActivos ? 'Ninguna flashcard coincide con los filtros.' : 'No hay flashcards. Crea la primera.'}
              </Text>
            </Stack>
          </Card>
        ) : (
          <Card withBorder radius="lg" p="lg" style={cardStyle}>
            <Table.ScrollContainer minWidth={720}>
              <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" withRowBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Pregunta</Table.Th>
                    <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Especialidad</Table.Th>
                    <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Tema</Table.Th>
                    <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Estado</Table.Th>
                    <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12, width: 90, textAlign: 'right' }}>Acciones</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginatedFlashcards.map((flashcard) => (
                    <Table.Tr key={flashcard.id}>
                      <Table.Td>
                        <Tooltip label={flashcard.pregunta} multiline w={420} withArrow position="top-start">
                          <Text size="xs" fw={500} ta="left" style={{ color: textColor, cursor: 'help', whiteSpace: 'nowrap' }}>
                            {resumenPregunta(flashcard.pregunta)}
                          </Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" fw={500} ta="left" style={{ color: colEspecialidad }}>{flashcard.especialidadNombre}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" fw={500} ta="left" style={{ color: colTema }}>{flashcard.temaNombre}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" fw={600} ta="left" style={{ color: flashcard.activo ? colActiva : colInactiva }}>
                          {flashcard.activo ? 'Activa' : 'Inactiva'}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Menu shadow="md" width={160} position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" size="md" aria-label="Acciones">
                              <IconSettings size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => handleOpenForm(flashcard)}>
                              Editar
                            </Menu.Item>
                            <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => handleDelete(flashcard.id)}>
                              Eliminar
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            {/* Footer: "Mostrando X de Y" + tamaño de página (izq), paginación (der) */}
            <Group justify="space-between" align="center" mt="md" wrap="wrap" gap="sm">
              <Group gap="xs" align="center">
                <Text size="xs" style={{ color: mutedColor }}>
                  Mostrando {paginatedFlashcards.length} de {filteredFlashcards.length.toLocaleString('es-MX')}
                </Text>
                <Select
                  size="xs"
                  w={92}
                  data={['10', '20', '50', '100']}
                  value={String(itemsPerPage)}
                  onChange={(v) => v && setItemsPerPage(Number(v))}
                  allowDeselect={false}
                  aria-label="Flashcards por página"
                />
              </Group>
              {totalPages > 1 && (
                <Pagination value={page} onChange={setPage} total={totalPages} color="gray" size="sm" />
              )}
            </Group>
          </Card>
        )}
      </Stack>

      {/* Modal de formulario */}
      <Modal
        opened={opened}
        onClose={close}
        title={editingFlashcard ? 'Editar Flashcard' : 'Nueva Flashcard'}
        size="lg"
      >
        <Stack gap="md">
          <Textarea
            label="Pregunta"
            placeholder="Escribe la pregunta..."
            required
            minRows={2}
            value={formData.pregunta}
            onChange={(e) => setFormData({ ...formData, pregunta: e.target.value })}
          />
          
          <Textarea
            label="Respuesta"
            placeholder="Escribe la respuesta..."
            required
            minRows={2}
            value={formData.respuesta}
            onChange={(e) => setFormData({ ...formData, respuesta: e.target.value })}
          />
          
          <Textarea
            label="Explicación (Opcional)"
            placeholder="Escribe una explicación adicional..."
            minRows={2}
            value={formData.explicacion}
            onChange={(e) => setFormData({ ...formData, explicacion: e.target.value })}
          />
          
          <Select
            label="Especialidad"
            placeholder="Selecciona una especialidad"
            required
            value={selectedEspecialidadId ? selectedEspecialidadId.toString() : null}
            onChange={(value) => {
              console.log('🟢 Especialidad seleccionada en MODAL:', value);
              const id = value ? parseInt(value) : null;
              console.log('🟢 ID parseado:', id);
              setSelectedEspecialidadId(id);
              // Limpiar temaId y los temas disponibles al cambiar especialidad
              setFormData({ ...formData, especialidadId: id || 0, temaId: 0 });
              setTemas([]); // Limpiar lista de temas inmediatamente
            }}
            data={especialidades.map(esp => ({
              value: esp.id.toString(),
              label: esp.nombre
            }))}
          />
          
          <Select
            label="Tema"
            placeholder="Selecciona un tema"
            required
            disabled={!selectedEspecialidadId}
            value={formData.temaId ? formData.temaId.toString() : null}
            onChange={(value) => {
              console.log('🎯 Tema seleccionado en MODAL:', value);
              setFormData({ ...formData, temaId: value ? parseInt(value) : 0 });
            }}
            data={temas.map(tema => ({
              value: tema.id.toString(),
              label: tema.nombre
            }))}
            key={selectedEspecialidadId?.toString() || 'no-especialidad'}
            searchable
            clearable
            nothingFoundMessage="No hay temas disponibles"
          />
          
          <Checkbox
            label="Activa"
            checked={formData.activo}
            onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
          />
          
          <Group justify="flex-end">
            <Button variant="outline" onClick={close}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} loading={formLoading}>
              {editingFlashcard ? 'Actualizar' : 'Crear'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal Importar CSV */}
      <Modal
        opened={csvModalOpen}
        onClose={() => { setCsvModalOpen(false); setCsvFile(null); setCsvResultado(null); }}
        title="Importar Flashcards desde CSV"
        size="lg"
        centered
      >
        <Stack gap="md">
          <Alert color="blue" icon={<IconFileSpreadsheet size={16} />}>
            <Text size="sm" fw={600} mb={4}>Formato esperado del CSV:</Text>
            <Text size="xs" style={{ fontFamily: 'monospace' }}>
              Pregunta, Respuesta, Explicación, CLAVE, SUBCLAVE
            </Text>
            <Text size="xs" mt={4} c="dimmed">
              CLAVE: 1=Pediatría, 2=Medicina Interna, 3=Cirugía General, 4=Ginecología y Obstetricia, 5=Urgencias<br/>
              SUBCLAVE: ID del tema (1–28)
            </Text>
          </Alert>

          {!csvResultado ? (
            <>
              <Box
                component="label"
                htmlFor="csv-upload-fc"
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', minHeight: 100,
                  border: '2px dashed #cbd5e1', borderRadius: 8,
                  cursor: 'pointer', gap: 8,
                }}
              >
                <IconUpload size={32} color="gray" />
                <Text size="sm" fw={500}>Haz clic para seleccionar un archivo CSV</Text>
                <Text size="xs" c="dimmed">Solo archivos .csv</Text>
                <input
                  id="csv-upload-fc"
                  type="file"
                  accept=".csv"
                  title="Seleccionar archivo CSV de flashcards"
                  aria-label="Seleccionar archivo CSV de flashcards"
                  className="csv-hidden-input"
                  onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
                  disabled={csvUploading}
                />
              </Box>

              {csvFile && (
                <Alert color="green" icon={<IconCheck size={16} />}>
                  Archivo seleccionado: <strong>{csvFile.name}</strong> ({(csvFile.size / 1024).toFixed(1)} KB)
                </Alert>
              )}

              {csvUploading && <Progress value={100} animated />}

              <Group justify="flex-end">
                <Button variant="light" onClick={() => setCsvModalOpen(false)} disabled={csvUploading}>Cancelar</Button>
                <Button leftSection={<IconUpload size={16} />} onClick={handleImportarCSV} disabled={!csvFile} loading={csvUploading} color="green">
                  Importar
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Alert color={csvResultado.errores.length > 0 ? 'yellow' : 'green'} icon={<IconCheck size={16} />}>
                <Text fw={600}>Importación completada</Text>
                <Text size="sm">{csvResultado.insertados} flashcards importadas correctamente</Text>
                <Text size="sm">{csvResultado.omitidos} filas omitidas</Text>
              </Alert>
              {csvResultado.errores.length > 0 && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} title="Errores:">
                  <List size="xs">
                    {csvResultado.errores.map((e, i) => <List.Item key={i}>{e}</List.Item>)}
                  </List>
                </Alert>
              )}
              <Group justify="flex-end">
                <Button onClick={() => { setCsvModalOpen(false); setCsvFile(null); setCsvResultado(null); }}>Cerrar</Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Box>
    </PageTransition>
  );
};

export default AdminFlashcardsPage;